import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useStore } from '../store/useStore';
import { Property, Tenant } from '../types/properties';
import { PropertyCard } from '../components/properties/PropertyCard';
import { AddPropertyForm } from '../components/properties/AddPropertyForm';
import { Modal } from '../components/Modal';
import { Building, Plus, Search, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function Properties() {
  const navigate = useNavigate();
  const { currentUser } = useStore();
  const [properties, setProperties] = useState<Property[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (currentUser?.teamId) {
      fetchData();
    }
  }, [currentUser]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [{ data: pData }, { data: tData }] = await Promise.all([
        supabase.from('properties').select('*').eq('team_id', currentUser?.teamId).order('created_at', { ascending: false }),
        supabase.from('tenants').select('*').eq('status', 'active')
      ]);
      setProperties(pData || []);
      setTenants(tData || []);
    } catch (error) {
      console.error('Error fetching properties data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredProperties = properties.filter(p => {
    const matchesSearch = p.address.toLowerCase().includes(searchQuery.toLowerCase()) || p.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]">
      <div className="flex items-center justify-between px-8 py-6 bg-[var(--t-surface)] border-b border-[var(--t-border)] shrink-0">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-[var(--t-primary-dim)] flex items-center justify-center">
            <Building className="text-[var(--t-primary)]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--t-text)] m-0">Properties</h1>
            <p className="text-sm text-[var(--t-text-muted)] mt-1">Manage your portfolio</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)]" size={16} />
            <input
              type="text"
              placeholder="Search properties..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] w-64"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)]" size={16} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2 bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] appearance-none"
            >
              <option value="all">All Statuses</option>
              <option value="occupied">Occupied</option>
              <option value="vacant">Vacant</option>
              <option value="listed-for-rent">Listed For Rent</option>
              <option value="under-maintenance">Under Maintenance</option>
            </select>
          </div>

          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--t-primary)] text-white text-sm font-semibold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors shadow-lg shadow-[var(--t-primary-dim)]"
          >
            <Plus size={16} /> Add Property
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-8 bg-[var(--t-bg)]">
        {isLoading ? (
          <div className="text-[var(--t-text-muted)]">Loading properties...</div>
        ) : filteredProperties.length === 0 ? (
          <div className="text-center p-12 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl">
            <Building size={48} className="mx-auto text-[var(--t-border-hover)] mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Properties Found</h3>
            <p className="text-[var(--t-text-muted)] mb-6">Get started by adding your first property to the portfolio.</p>
            <button onClick={() => setShowAddModal(true)} className="px-6 py-2 bg-[var(--t-primary)] text-white font-semibold rounded-xl hover:bg-[var(--t-primary-hover)]">
              Add Property
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProperties.map(property => (
              <PropertyCard
                key={property.id}
                property={property}
                tenant={tenants.find(t => t.property_id === property.id)}
                onClick={() => navigate(`/properties/${property.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add New Property" maxWidth="max-w-2xl">
        <AddPropertyForm onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); fetchData(); }} />
      </Modal>
    </div>
  );
}
